import { formatPhone } from '~/utils/strings';

/** UK national mobile, digits only (same `07` + 9 digits as the tail of UK_PHONE_REGEX). */
const isUkNationalMobileDigits = (digits: string): boolean => /^07\d{9}$/.test(digits);

/** Strip leading 00 (international access) so 0044… matches UK E.164 rules as 44…. */
const stripLeadingInternationalAccessDigits = (digits: string): string => {
  let d = digits;
  while (d.length > 2 && d.startsWith('00')) {
    d = d.slice(2);
  }
  return d;
};

/** UK national mobile (07…) or E.164 (+44…) display; null if not a recognized full UK shape. */
const formatUkDigitsForDisplay = (digits: string): string | null => {
  if (isUkNationalMobileDigits(digits)) {
    return `${digits.slice(0, 5)} ${digits.slice(5)}`;
  }
  if (/^447\d{9}$/.test(digits)) {
    return `+44 ${digits.slice(2, 6)} ${digits.slice(6)}`;
  }
  if (/^44\d{10}$/.test(digits)) {
    return `+44 ${digits.slice(2, 4)} ${digits.slice(4, 8)} ${digits.slice(8)}`;
  }
  if (/^44\d{11}$/.test(digits)) {
    return `+44 ${digits.slice(2, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`;
  }
  return null;
};

/** UK national mobile as the user types: 07 + up to 9 more digits (max 11 total). */
const formatUkNationalMobileProgressive = (digits: string): string | null => {
  if (!/^07\d{0,9}$/.test(digits)) {
    return null;
  }
  if (digits.length <= 5) {
    return digits;
  }
  return `${digits.slice(0, 5)} ${digits.slice(5)}`;
};

/**
 * UK mobile in E.164 as digits only: `447` + 8–9 subscriber digits (11–12 chars total).
 * Formats as `+44 7123 456789` (same 4+6 grouping as formatUkDigitsForDisplay for full mobiles).
 *
 * US area code 447 exists, so we only run from 11 digits onward (never a valid 10-digit NANP
 * without a leading country code 1). Shorter `447…` input is left to formatPhone / raw digits.
 */
const formatUkE164MobileProgressive = (digits: string): string | null => {
  if (digits.length < 11 || !/^447\d{0,9}$/.test(digits)) {
    return null;
  }
  const rest = digits.slice(2);
  return `+44 ${rest.slice(0, 4)} ${rest.slice(4)}`;
};

export const formatPhoneNumberForDisplay = (phone: string | null | undefined): string => {
  if (!phone) {
    return '';
  }
  const trimmed = phone.trim();
  const digitsRaw = trimmed.replace(/\D/g, '');
  if (!digitsRaw) {
    return trimmed;
  }
  const digits = stripLeadingInternationalAccessDigits(digitsRaw);

  if (digits === '44') {
    return '+44';
  }

  // Before US formatPhone: 07… must never hit the 11-digit 3-4-4 rule (→ "071 2345 6789").
  const ukNationalProgressive = formatUkNationalMobileProgressive(digits);
  if (ukNationalProgressive !== null) {
    return ukNationalProgressive;
  }

  const ukE164MobileProgressive = formatUkE164MobileProgressive(digits);
  if (ukE164MobileProgressive !== null) {
    return ukE164MobileProgressive;
  }

  const ukFormatted = formatUkDigitsForDisplay(digits);
  if (ukFormatted) {
    return ukFormatted;
  }
  const formatted = formatPhone(trimmed);
  // formatPhone falls back to a plain digit string for non-US shapes — treat that as "unformatted"
  if (formatted && formatted !== digitsRaw && formatted !== digits) {
    return formatted;
  }
  // Prefix + for other 44… stored forms, but not partial 447… while typing (could be US area 447).
  if (digits.startsWith('44') && !(/^447\d*$/.test(digits) && digits.length < 11)) {
    return `+${digits}`;
  }
  return formatted || trimmed;
};

// Same logic is used on mobile
export const displayPhoneLastFour = (value: string) => {
  if (!value) {
    return '';
  }

  const digits = stripLeadingInternationalAccessDigits(value.replace(/\D/g, ''));

  if (digits.length < 4) {
    return digits;
  }

  const lastFour = digits.slice(-4);

  // UK: national mobile 07… (11 digits) or E.164 +44… (typically 12–13 digits)
  if (
    isUkNationalMobileDigits(digits) ||
    (digits.length >= 12 && digits.length <= 13 && digits.startsWith('44'))
  ) {
    return `**** *** ${lastFour}`;
  }

  // US: 10 digits or 11 starting with 1
  if (
    digits.length === 10 ||
    (digits.length === 11 && digits.startsWith('1'))
  ) {
    return `(***) ***-${lastFour}`;
  }

  // Fallback (safe default)
  return `${'*'.repeat(digits.length - 4)}${lastFour}`;
};
